import { Component, Element, Event, EventEmitter, Host, Prop, State, Watch, h } from '@stencil/core';
import { uuidv4, en, awaitTopbar } from '@/globals/helpers';
import { minus, plus } from 'pn-design-assets/pn-assets/icons.js';
import { PnLanguages } from '@/globals/types';
import { translations } from './translations';

/**
 * The `pn-counter` is an input with a button on each side that can be used to increase and decrease the input value.
 *
 * @nativeInput Use the `input` event to listen to content being modified by the user. It is emitted everytime a user writes or removes content in the input.
 * @nativeChange The `change` event is emitted when the input loses focus, the user clicks `Enter` or makes a selection (such as auto complete or suggestions).
 */
@Component({
  tag: 'pn-counter',
  styleUrl: 'pn-counter.scss',
})
export class PnCounter {
  id: string = `pn-counter-${uuidv4()}`;

  idLabel: string = `${this.id}-label`;
  idText: string = `${this.id}-text`;
  idAmount: string = `${this.id}-count`;

  counterElement: HTMLInputElement;

  @Element() hostElement: HTMLElement;

  @State() clearAriaTimer: NodeJS.Timeout;
  @State() displaySrValue: boolean = false;
  @State() showMinMaxMessage: boolean = false;
  /** Prevent double events when reaching min/max values. */
  @State() lastDispatchedValue: number;
  @State() interactType: 'input' | 'decrease' | 'increase';

  /** Label for the counter */
  @Prop() label!: string;
  /** Set a helpertext for the counter. */
  @Prop() helpertext?: string;
  /** Set a predefined value */
  @Prop({ mutable: true, reflect: true }) value: number = 0;
  /** Set a unique HTML ID for the counter */
  @Prop() counterid: string = this.id;
  /** Set the language manually for the built in translations. */
  @Prop() language?: PnLanguages = null;

  /** Text for the decrease button. Default is "Decrease". @category Translation */
  @Prop() labelDecrease?: string;
  /** Text for the increase button. Default is "Increase". @category Translation */
  @Prop() labelIncrease?: string;
  /** Set a message that shows below input when user wants to set a value that is below `min`. Default is "Minimum value is x" @category Translation */
  @Prop() minMessage?: string;
  /** Set a message that shows below input when user wants to set a value that is above `max`. Default is "Maximum value is x"  @category Translation */
  @Prop() maxMessage?: string;

  /** HTML input name. @category HTML input */
  @Prop() name?: string;
  /** Minimum value. @category HTML input */
  @Prop() min: number;
  /** Maximum value. @category HTML input */
  @Prop() max: number;
  /** Increase/decrease the value in steps, default is 1. @category HTML input */
  @Prop() step: number = 1;
  /** Suggest values for the counter input. @category HTML input */
  @Prop() list?: string;

  /** Set the counter as required. @category State */
  @Prop() required: boolean = false;
  /** Set the counter as readonly. @category State */
  @Prop() readonly: boolean = false;
  /** Set the counter as disabled. @category State */
  @Prop() disabled: boolean = false;

  /**
   * Instead of listening to multiple input, change and/or click events, we bundled them into one.
   * - `value` is the current counter value.
   * - `input` is true if the user changed the value with the HTML input.
   * - `decrease` is true if the user clicked on the decrease button.
   * - `increase` is true if the user clicked on the increase button.
   */
  @Event() counterInput: EventEmitter<{ value: number; input?: boolean; decrease?: boolean; increase?: boolean }>;

  @Watch('value')
  watchValue() {
    this.showSrContent();

    this.showMinMaxMessage = false;

    if (this.hasMin() && this.value < this.min) {
      this.setMin();
      this.showMinMaxMessage = true;
    }

    if (this.hasMax() && this.value > this.max) {
      this.setMax();
      this.showMinMaxMessage = true;
    }

    if (this.lastDispatchedValue !== this.value)
      this.counterInput.emit({
        value: this.value,
        input: this.interactType === 'input',
        decrease: this.interactType === 'decrease',
        increase: this.interactType === 'increase',
      });

    this.lastDispatchedValue = this.value;

    this.handleInputLength();
  }

  @Watch('counterid')
  handleId() {
    this.idLabel = `${this.counterid}-label`;
    this.idText = `${this.counterid}-text`;
    this.idAmount = `${this.counterid}-count`;
  }

  async componentWillLoad() {
    this.handleId();
    if (this.language) return;
    await awaitTopbar(this.hostElement);
  }

  componentDidLoad() {
    this.counterElement = this.hostElement.querySelector<HTMLInputElement>('.pn-counter-input');
    this.handleInputLength();
  }

  setVal(event: InputEvent) {
    const { valueAsNumber } = event.target as HTMLInputElement;

    if (isNaN(valueAsNumber)) return;

    this.interactType = 'input';
    this.value = valueAsNumber;
  }

  setMin() {
    if (this.hasMin()) this.value = this.min;
  }

  setMax() {
    if (this.hasMax()) this.value = this.max;
  }

  hasMin(): boolean {
    return typeof this.min === 'number';
  }

  hasMax(): boolean {
    return typeof this.max === 'number';
  }

  decreaseAmount() {
    if (this.hasMin() && this.value <= this.min) this.showMinMaxMessage = true;

    this.interactType = 'decrease';
    this.value = this.value - this.step;
  }

  increaseAmount() {
    if (this.hasMax() && this.value >= this.max) this.showMinMaxMessage = true;

    this.interactType = 'increase';
    this.value = this.value + this.step;
  }

  keyBoardInput(event: KeyboardEvent) {
    event.stopImmediatePropagation();

    if (!/^(Home|End)$/.test(event.key)) return;

    event.preventDefault();

    this.interactType = 'input';
    if (event.key === 'Home') this.setMin();
    if (event.key === 'End') this.setMax();
  }

  showSrContent() {
    if (this.clearAriaTimer) {
      clearTimeout(this.clearAriaTimer);
    }

    this.displaySrValue = true;

    this.clearAriaTimer = setTimeout(() => {
      this.displaySrValue = false;
    }, 4000);
  }

  toggleMinMaxMessage(): string {
    const isMax = this.value === this.max;
    const i18ntxt = `${isMax ? 'MAX' : 'MIN'}_VALUE_MESSAGE`;

    const message = this.translate(i18ntxt) + `${isMax ? this.max : this.min}`.toString();

    return isMax ? this.maxMessage || message : this.minMessage || message;
  }

  getTextMessage(showValue: boolean = false): string {
    if (this.showMinMaxMessage) return this.toggleMinMaxMessage();
    return showValue ? this.value.toString() : this.helpertext;
  }

  describedbyIds(): string {
    const list = [];
    if (this.helpertext) list.push(this.idText);
    if (this.displaySrValue) list.push(this.idAmount);
    if (list.length === 0) return null;
    return list.join(' ');
  }

  translate(prop: string): string {
    return translations?.[prop]?.[this.language || en];
  }

  noButtons(): boolean {
    return this.disabled || this.readonly;
  }

  handleInputLength() {
    const ch = this.max ? this.max.toString() : this.value.toString();
    if (ch.length > 3) this.counterElement.style.setProperty('--pn-counter-ch', `${ch.length - 3}ch`);
    else this.counterElement.style.setProperty('--pn-counter-ch', '0ch');
  }

  render() {
    return (
      <Host>
        <div class="pn-counter" role="group" aria-labelledby={this.idLabel} aria-describedby={this.describedbyIds()}>
          <label htmlFor={this.counterid} class="pn-counter-label" id={this.idLabel}>
            <span>{this.label}</span>
          </label>
          <div class="pn-counter-items" data-hidebuttons={this.noButtons()}>
            <pn-button
              data-decrease
              appearance="light"
              variant="outlined"
              no-tab={this.noButtons()}
              icon={minus}
              iconOnly={true}
              arialabel={this.labelDecrease || this.translate('DECREASE')}
              onClick={() => this.decreaseAmount()}
            />
            <input
              id={this.counterid}
              class="pn-counter-input"
              type="number"
              min={this.min}
              max={this.max}
              step={this.step}
              value={this.value}
              name={this.name}
              required={this.required}
              readonly={this.readonly}
              disabled={this.disabled}
              aria-describedby={this.describedbyIds()}
              onInput={e => this.setVal(e)}
              onKeyDown={(e: KeyboardEvent) => this.keyBoardInput(e)}
            />
            <pn-button
              data-increase
              appearance="light"
              variant="outlined"
              no-tab={this.noButtons()}
              icon={plus}
              iconOnly={true}
              arialabel={this.labelIncrease || this.translate('INCREASE')}
              onClick={() => this.increaseAmount()}
            />
          </div>

          {!!this.getTextMessage() && (
            <p id={this.idText} class="pn-counter-helpertext">
              <span>{this.getTextMessage()}</span>
            </p>
          )}

          <p id={this.idAmount} class="pn-counter-sr-only" aria-live="assertive">
            {this.displaySrValue ? this.getTextMessage(true) : ''}
          </p>
        </div>
      </Host>
    );
  }
}
